<script lang="ts" setup>
import { useUserStore } from "~/stores/user"
import { storeToRefs } from "pinia"
import { PROFILE_SLUGS } from "~/utils/constants"
import type { DiscountCodeProps } from "~/components/Card/DiscountCode.props"

const userStore = useUserStore()
const authToken = useStatefulCookie("authToken")
const { user } = storeToRefs(userStore)
const route = useRoute()

const myAccountArea = computed(
  () => route.fullPath.includes(PROFILE_SLUGS.ROOT) ?? false
)

// Welcome modal logic in checkout
const WELCOME_WORD = "benvenuto"
const registrationCookie = useStatefulCookie("show-registration-modal")
const { discountCodes } = useDiscountCodes()
const registrationDiscountCode = ref<DiscountCodeProps | null>(null)
const { openModal, closeModal } = useModal()

// Function to check and show the welcome modal
const checkAndShowWelcomeModal = () => {
  const welcomeDiscountCodes = discountCodes.value?.filter(
    (e: DiscountCodeProps) =>
      e?.status == "ready" &&
      e?.discountCode?.toLowerCase()?.includes(WELCOME_WORD) &&
      e?.symbol === "%"
  )

  registrationDiscountCode.value = welcomeDiscountCodes[0] ?? null

  if (
    registrationCookie.value === true &&
    authToken.value &&
    import.meta.client &&
    !!registrationDiscountCode.value
  ) {
    openModal("welcome-discount-checkout")
    registrationCookie.value = undefined
  }
}

// Watcher for discount codes
watch(discountCodes, checkAndShowWelcomeModal)

// Watcher for registration cookie and auth token to handle checkout case
watch(
  [registrationCookie, authToken],
  () => {
    if (
      registrationCookie.value === true &&
      authToken.value &&
      import.meta.client
    ) {
      // Wait a bit to ensure discount codes are loaded
      nextTick(() => {
        setTimeout(checkAndShowWelcomeModal, 100)
      })
    }
  },
  { immediate: true }
)

// Handle coupon application from welcome modal
const handleCouponApplied = async () => {
  try {
    // Force update of the DiscountCode component by emitting the same events it listens to
    if (import.meta.client) {
      // Emit events to signal coupon application start
      window.dispatchEvent(new CustomEvent("start-submitting-coupon"))

      // Emit a custom event that forces components to sync their props
      window.dispatchEvent(new CustomEvent("coupon-applied-external"))

      // Also emit the standard submit-coupon event that components might listen to
      window.dispatchEvent(new CustomEvent("submit-coupon"))

      // Emit a custom event to force order refresh
      window.dispatchEvent(new CustomEvent("force-order-refresh"))

      // Emit events to signal coupon application end
      window.dispatchEvent(new CustomEvent("finish-submitting-coupon"))
    }

    // Wait for Vue's reactivity to propagate
    await nextTick()
  } catch (error) {
    console.error("Error handling coupon application:", error)
  }
}

onMounted(async () => {
  if (!myAccountArea.value) {
    const checkSession = await useCheckSessionUser()
    if (authToken.value)
      await userStore.updateNewsletterSubscription(authToken.value)
    if (typeof checkSession === "object" && !user.value?.email)
      userStore.updateUserData(checkSession)
  }
})
</script>

<template>
  <div>
    <!-- Welcome modal for checkout -->
    <DialogsAndModalsUtilsModal
      id="welcome-discount-checkout"
      :enable-on-click-outside="true"
      fullscreen
    >
      <DialogsAndModalsWelcomeDiscount
        :coupon="registrationDiscountCode"
        :continue-checkout="true"
        @on-close="closeModal('welcome-discount-checkout')"
        @coupon-applied="handleCouponApplied"
      />
    </DialogsAndModalsUtilsModal>

    <slot />
  </div>
</template>
